// Candidate slate submission (`#pipeline/<R>?action=slate`, DS §1.9, DC-101, P-7): the Recruiter's chosen qualified
// candidates, each with the Recruiter's one-line read against the must-haves, submitted to the requisition's Hiring
// Manager in one act — "Recruiter submits qualified slate in app; HM receives action-center task and due date." The
// read is assembled (`candidate.batch_assemble`) and the slate sent (`candidate.batch_send`), which opens the HM's dated
// review task and starts the review SLA; the due date is shown on the cards once the slate is In HM Review. An internal
// candidate without the self-attestation is refused by the server with his BRD §9 sentence.

import { useState } from "react";
import type { ApplicationCard } from "@/api/tools";
import { candidateBatchAssemble, candidateBatchSend } from "@/api/tools";
import { useWrite } from "@/api/useTool";
import { ACTION_LABELS, CheckTile, Dialog, Field, G1_LABELS, RULE_LABELS, Stack, TextArea } from "@/mc";
import { PROFILE_RULES } from "@/views/candidate/labels";
import { PIPELINE_LABELS, PIPELINE_RULES } from "./labels";
import { MIN_NOTES, nameOf } from "./pipeline";
import { pipelineRefusal } from "./refusals";

export function SlateDialog({ rid, candidates, onClose }: { rid: string; candidates: readonly ApplicationCard[]; onClose: (sent: boolean) => void }) {
  const [reads, setReads] = useState<Record<string, { include: boolean; note: string }>>(
    () => Object.fromEntries(candidates.map((card) => [card.id, { include: true, note: card.recruiter_note ?? "" }])),
  );
  const [refused, setRefused] = useState<string | null>(null);
  const inval = [["pipeline"], ["candidates", rid], ["requisition", rid], ["today"]] as const;
  const assemble = useWrite((input: Parameters<typeof candidateBatchAssemble>[0], key) => candidateBatchAssemble(input, key), inval);
  const send = useWrite((input: Parameters<typeof candidateBatchSend>[0], key) => candidateBatchSend(input, key), inval);
  const chosen = candidates.filter((card) => reads[card.id]?.include);
  const ready = chosen.length > 0 && chosen.every((card) => (reads[card.id]?.note.trim().length ?? 0) >= MIN_NOTES);
  const busy = assemble.isPending || send.isPending;

  const submit = () => {
    setRefused(null);
    assemble
      .mutateAsync({ requisition_id: rid, reads: chosen.map((card) => ({ application_id: card.id, recruiter_note: reads[card.id]?.note.trim() ?? "" })) })
      .then(() => send.mutateAsync({ requisition_id: rid, application_ids: chosen.map((card) => card.id) }))
      .then(() => onClose(true))
      .catch((error: unknown) => setRefused(pipelineRefusal(error) ?? RULE_LABELS.failedSyncRetryable));
  };

  return (
    <Dialog
      open
      title={PIPELINE_LABELS.candidateSlateSubmission}
      onClose={() => onClose(false)}
      hint={PIPELINE_RULES.slate}
      refused={refused ? { reason: refused } : undefined}
      primary={{ label: ACTION_LABELS.submit, onClick: submit, busy, blocked: ready ? undefined : { reason: PIPELINE_RULES.slate }, hs: "pipe.slate.submit" }}
      
    >
      <Stack gap={12}>
        {candidates.map((card, index) => {
          const read = reads[card.id] ?? { include: true, note: "" };
          return (
            <Stack key={card.id} gap={6}>
              <CheckTile
                checked={read.include}
                onChange={(include) => setReads({ ...reads, [card.id]: { ...read, include } })}
                sub={card.internal ? PROFILE_RULES.attestation : undefined}
                
              >
                {nameOf(card)}
              </CheckTile>
              {read.include ? (
                <Field label={G1_LABELS.rationale} required>
                  <TextArea rows={2} value={read.note} onChange={(event) => setReads({ ...reads, [card.id]: { ...read, note: event.target.value } })}  />
                </Field>
              ) : null}
            </Stack>
          );
        })}
      </Stack>
    </Dialog>
  );
}
